import * as React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import weakKey from 'weak-key';

import { Typography } from '../../../components/atoms';
import {
  Location as OrderAreaIcon,
  OrderCollection as OrderCollectionIcon,
  OrderDelivery as OrderDeliveryIcon,
  OrderGate as OrderGateIcon,
  OrderParking as OrderParkingIcon,
  OrderRoom as OrderRoomIcon,
  OrderTableService as OrderTableServiceIcon,
  OrderVilla as OrderVillaIcon,
} from '../../../components/icons';
import { dateTools } from '../../../utils/tools';
import * as Api from '../../../weborder/Api';
import { ToCurrency } from '../../../weborder/BasketControl';

type LocationDetailsType = {
  address: JSX.Element | string;
  icon: JSX.Element;
  title: string;
};
type Props = {
  configData: Api.AppConfig;
  currency: Api.Currency;
  deliveryLocation: Api.DeliveryLocation | null;
  languageCode: string;
  restaurantData: Api.CollectionRestaurantDetail | Api.DeliveryRestaurantDetail;
  translate: any;
};

const useStyles = makeStyles((theme: Theme) => ({
  locationHeader: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0, 2),
  },

  details: {
    alignItems: 'flex-start',
    display: 'flex',
    flexFlow: 'column nowrap',

    '& svg': {
      left: 0,
      position: 'absolute',
      top: 'calc(50% - 14px)',
    },

    '& .MuiTypography-root': {
      lineHeight: theme.typography.pxToRem(16),
    },
  },

  orderType: {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(1),
    position: 'relative',

    '& svg': {
      left: 0,
      position: 'absolute',
      top: 'calc(50% - 13px)',
    },
  },

  address: {
    color: theme.palette.grey[600],
  },

  restaurantNotes: {
    alignItems: 'center',
    color: theme.palette.grey[600],
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'flex-start',
    listStyleType: 'none',
    marginTop: 0,
    paddingLeft: 0,

    '& li': {
      alignItems: 'center',
      display: 'flex',
      flexFlow: 'row nowrap',
      paddingLeft: 0,
      paddingRight: theme.spacing(1.5),

      '&::before': {
        color: theme.palette.grey[600],
        content: '"\\00b7"',
        display: 'inline-block',
        fontSize: theme.typography.pxToRem(24),
        lineHeight: theme.typography.pxToRem(16),
        textIndent: `-9px`,
      },
    },

    '& li:first-of-type': {
      '&::before': {
        content: '""',
      },
    },
  },

  [theme.breakpoints.up('sm')]: {
    locationHeader: {
      padding: theme.spacing(0, 3),
    },
  },

  [theme.breakpoints.up('md')]: {
    locationHeader: {
      padding: 0,
    },

    details: {
      flexDirection: 'row',
      marginBottom: theme.spacing(1),

      '& .title': {
        marginRight: theme.spacing(1),
      },
    },

    orderType: {
      marginBottom: 0,
    },

    address: {
      color: theme.palette.common.black,
      fontSize: theme.typography.fontSize,
    },

    restaurantNotes: {
      padding: 0,
    },
  },
}));

export function LocationHeader(props: Props): JSX.Element | null {
  const {
    configData,
    currency,
    deliveryLocation,
    languageCode,
    restaurantData,
    translate,
  } = props;

  const {
    Area,
    DriveThrough,
    Gate,
    Generic,
    House,
    ParkingBay,
    Room,
    Table,
    Villa,
  } = Api.TableServiceCategory;

  const classes = useStyles();
  const { formatTime, getClosingTime } = dateTools;

  const formatAddress = (address, addresslayout) => {
    const building = address?.Building ? `${address.Building},` : null;
    const postCode = address?.PostCode ? `${address.PostCode},` : null;

    if (addresslayout === Api.AddressLayoutType.FullStreetAddressFirst) {
      return (
        <>
          <Typography className={classes.address} variant="body2">
            {address.Street}, {building}
          </Typography>
          <Typography className={classes.address} variant="body2">
            {address.Town} {postCode}
          </Typography>
        </>
      );
    }

    return (
      <Typography className={classes.address} variant="body2">
        {`${building} ${address.Street}`}
      </Typography>
    );
  };

  const getCollectionIcon = (
    isTableServiceOnly: boolean,
    tableServiceCategory: Api.TableServiceCategory,
  ): JSX.Element => {
    if (!isTableServiceOnly) {
      return <OrderCollectionIcon />;
    }

    switch (tableServiceCategory) {
      case House:
      case Villa:
        return <OrderVillaIcon />;
      case Room:
        return <OrderRoomIcon />;
      case Area:
        return <OrderAreaIcon />;
      case DriveThrough:
      case ParkingBay:
        return <OrderParkingIcon />;
      case Gate:
        return <OrderGateIcon />;
      case Generic:
      case Table:
      default:
        return <OrderTableServiceIcon />;
    }
  };

  const getCollectionTitle = (
    isTableServiceOnly: boolean,
    tableServiceCategory: Api.TableServiceCategory,
    translate: any,
  ): string => {
    if (!isTableServiceOnly) {
      return translate('Menu_Location_CollectAt');
    }

    switch (tableServiceCategory) {
      case Generic:
      case Table:
        return translate('Table_service');
      case Villa:
        return translate('Villa_service');
      case House:
        return translate('House_service');
      case Room:
        return translate('Room_service');
      case Area:
        return translate('Area_service');
      case ParkingBay:
        return translate('Parking_bay_service');
      case Gate:
        return translate('Gate_service');
      case DriveThrough:
        return translate('Drive_Through');
      default:
        return translate('Order');
    }
  };

  const getDeliveryLocationDetails = (): LocationDetailsType => ({
    address: formatAddress(deliveryLocation, configData.DeliveryAddressLayout),
    icon: <OrderDeliveryIcon />,
    title: translate('Delivery'),
  });

  const getCollectionLocationDetails = (
    data: Api.CollectionRestaurantDetail,
  ): LocationDetailsType => {
    const hasTableService = data.PickupLocationTypes.some(
      (pickupLocation) =>
        pickupLocation === Api.PickupLocationType.TableService,
    );
    const isTableServiceOnly =
      data.PickupLocationTypes.length === 1 && hasTableService;
    const tableServiceCategory =
      data.TableServiceCatagory || Api.TableServiceCategory.Generic;

    return {
      address: (
        <Typography className={classes.address} variant="body2">
          {restaurantData.PhysicalRestaurantAddress}
        </Typography>
      ),
      icon: getCollectionIcon(isTableServiceOnly, tableServiceCategory),
      title: getCollectionTitle(
        isTableServiceOnly,
        tableServiceCategory,
        translate,
      ),
    };
  };

  const getRestaurantNotes = () => {
    const notes = [];

    if (restaurantData.DeliveryType === Api.DeliveryType.Delivery) {
      const deliveryFee = ToCurrency(
        restaurantData.DeliveryFee,
        currency,
        languageCode,
      );
      const deliveryFeeNote = `${translate(
        'Menu_Header_DeliveryFee',
      )}: ${deliveryFee}`;

      notes.push(deliveryFeeNote);
    }

    if (restaurantData.MinimumOrder > 0) {
      const minOrderAmount = ToCurrency(
        restaurantData.MinimumOrder,
        currency,
        languageCode,
      );
      const minOrderNote = `${translate(
        'Menu_Header_MinimumOrder',
      )}: ${minOrderAmount}`;

      notes.push(minOrderNote);
    }

    if (restaurantData.IsOpen) {
      const closingTime = getClosingTime(
        restaurantData.SecondsUntilRestaurantCloses,
      );
      const openTilTime = formatTime(closingTime);
      const openStatusNote = `${translate(
        'Menu_Header_OpenTill',
      )} ${openTilTime}`;

      notes.push(openStatusNote);
    }

    return notes;
  };

  const locationDetails =
    restaurantData.DeliveryType === Api.DeliveryType.Delivery
      ? getDeliveryLocationDetails()
      : getCollectionLocationDetails(restaurantData);

  const restaurantNotes = getRestaurantNotes();

  return (
    <div className={classes.locationHeader}>
      <div className={classes.details}>
        <div className={classes.orderType}>
          {locationDetails.icon}

          <Typography>
            <em>{locationDetails.title}</em>
          </Typography>
        </div>

        {locationDetails.address && locationDetails.address}
      </div>

      {!!restaurantNotes.length && (
        <ul className={classes.restaurantNotes}>
          {restaurantNotes.map((note) => (
            <li key={weakKey({ note })}>
              <Typography variant="body2">{note}</Typography>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
